import { motion } from "framer-motion";
import { Briefcase, ClipboardList, Smartphone } from "lucide-react";

/**
 * Persona-based "What's in it for me" section.
 * Three cards showing concrete day-in-the-life value for each user type.
 */
const personas = [
    {
        icon: Briefcase,
        role: "Ejer / Direktør",
        name: "Anders Iversen",
        company: "AI Company ApS",
        accent: "from-sky-500 to-sky-700",
        bg: "from-sky-50 to-white",
        border: "border-sky-200",
        chip: "bg-sky-100 text-sky-700",
        title: "Spar fredagen — fakturering med ét klik.",
        bullets: [
            "Live-overblik over driften — hvor som helst",
            "Rapporter klar til revisor uden bilagskaos",
            "Automatisk afregning af underleverandører",
            "Beslutninger på baggrund af data — ikke mavefornemmelse",
        ],
        stat: { v: "8 t", l: "sparet pr. uge på admin" },
    },
    {
        icon: ClipboardList,
        role: "Servicechef",
        name: "Mette Larsen",
        company: "Drift & kvalitet",
        accent: "from-emerald-500 to-emerald-700",
        bg: "from-emerald-50 to-white",
        border: "border-emerald-200",
        chip: "bg-emerald-100 text-emerald-700",
        title: "Live-overblik over alle medarbejdere på job.",
        bullets: [
            "Tildel opgaver med drag-and-drop på kalenderen",
            "Se status: planlagt / i gang / færdig",
            "Reklamationer auto-tildelt rette person",
            "Vikar tropper op? Alle nøgler og koder klar med ét klik",
        ],
        stat: { v: "12 t", l: "sparet pr. uge på koordinering" },
    },
    {
        icon: Smartphone,
        role: "Rengøringsmedarbejder",
        name: "Lone Nielsen",
        company: "På gulvet",
        accent: "from-violet-500 to-violet-700",
        bg: "from-violet-50 to-white",
        border: "border-violet-200",
        chip: "bg-violet-100 text-violet-700",
        title: "Tjek-ind, foto-dok, næste opgave — alt fra mobilen.",
        bullets: [
            "Tjek-ind og -ud direkte på kunden",
            "Foto-dokumentation indlejret i opgaven",
            "Reklamation oprettet på 30 sekunder",
            "Tidsregistrering automatisk — ingen papir",
        ],
        stat: { v: "4 t", l: "sparet pr. uge på papirarbejde" },
    },
];

export default function Personas() {
    return (
        <section
            className="py-24 md:py-32 bg-gradient-to-b from-slate-50/60 to-white border-y border-slate-200/70"
            data-testid="personas-section"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                        Hvem får hvad ud af det?
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                        Bygget til alle i jeres firma.
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                        Fra ejeren der vil sove roligt om søndagen — til medarbejderen der bruger app&apos;en
                        på gulvet hver dag. Cleany er designet til alle tre brugertyper.
                    </p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-5">
                    {personas.map((p, i) => (
                        <motion.div
                            key={p.role}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.55, delay: i * 0.1 }}
                            className={`relative rounded-2xl border-2 ${p.border} bg-gradient-to-b ${p.bg} p-7 overflow-hidden`}
                            data-testid={`persona-card-${p.role}`}
                        >
                            <div className="absolute top-0 right-0 h-32 w-32 -translate-y-12 translate-x-12 rounded-full bg-white/40 blur-2xl" />

                            <div className="relative">
                                {/* Top */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${p.accent} text-white flex items-center justify-center shadow-md`}>
                                        <p.icon className="h-5 w-5" strokeWidth={2} />
                                    </div>
                                    <span className={`inline-flex items-center rounded-full ${p.chip} px-3 py-1 text-[11px] font-bold uppercase tracking-wider`}>
                                        {p.role}
                                    </span>
                                </div>

                                {/* Persona */}
                                <div className="mt-5 text-sm text-slate-500">
                                    <span className="font-semibold text-slate-700">{p.name}</span> · {p.company}
                                </div>
                                <h3 className="mt-2 font-display text-lg md:text-xl font-bold text-slate-900 leading-snug">
                                    {p.title}
                                </h3>

                                {/* Bullets */}
                                <ul className="mt-5 space-y-2.5">
                                    {p.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-[13px] text-slate-700 leading-snug">
                                            <span className={`mt-1 h-1.5 w-1.5 rounded-full bg-gradient-to-br ${p.accent} shrink-0`} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Stat footer */}
                                <div className="mt-6 pt-4 border-t border-slate-200/70 flex items-baseline gap-2">
                                    <span className="font-display text-2xl font-bold text-slate-900 tabular-nums">
                                        {p.stat.v}
                                    </span>
                                    <span className="text-sm text-slate-600">{p.stat.l}</span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
